"use client";
import { IResource } from "@gettactic/api";
import { HookFormHiddenInput } from "@gettactic/components/src/form/HiddenInput/HookFormHiddenInput";
import { HookFormImageUploaderInput } from "@gettactic/components/src/form/ImageUploaderInput/HookFormImageUploaderInput";
import { HookFormNumberInput } from "@gettactic/components/src/form/NumberInput/HookFormNumberInput";
import { HookFormTagsInput } from "@gettactic/components/src/form/TagsInput/HookFormTagsInput";
import { HookFormTextEditorInput } from "@gettactic/components/src/form/TextEditorInput/HookFormTextEditorInput";
import { HookFormTextInput } from "@gettactic/components/src/form/TextInput/HookFormTextInput";
import { HookFormToggleInput } from "@gettactic/components/src/form/ToggleInput/HookFormToggleInput";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput/HookFormUnifiedSelectInput";
import { Stack } from "@gettactic/components/src/primitives/Stack";
import { capitalize } from "@gettactic/helpers";
import { cn } from "@gettactic/helpers/src/css/cn";
import { isLabelFieldEnabled } from "@gettactic/helpers/src/featureFlags";
import { getResourceCoverUrl } from "@gettactic/helpers/src/resources/getResourceCoverUrl";
import { useAreas, useAuthenticated, useGetAmenities } from "@gettactic/hooks";
import { DesktopComputerIcon, ViewGridIcon } from "@heroicons/react/solid";
import pluralize from "pluralize";
import React, { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { IoLayersSharp } from "react-icons/io5";
import { MdLocalParking } from "react-icons/md";
import { ResourceMakeUnavailableControl } from "@/features/reserve/components/controls/ResourceMakeUnavailableControl";
import { EditResourceForm } from "@/features/reserve/components/ResourceSettingsPanel/ResourceSettingsPanel";
import { ResourceTicketTypesField } from "@/features/settings/components/ResourceTicketTypesField";
import { RESOURCES_TEXT } from "@/features/settings/utils/ListResources";
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from "@/lib/hooks/useCustomParkingVerbiage";
import { EntityType } from "@/lib/types/EntityType";

const entityIconProps = Object.freeze({
  className: "h-10 w-10 text-white"
});
const EntityIcons: Record<EntityType, React.ReactNode> = Object.freeze({
  workspace: <DesktopComputerIcon {...entityIconProps} />,
  meeting_room: <ViewGridIcon {...entityIconProps} />,
  parking: <MdLocalParking {...entityIconProps} />,
  area: <IoLayersSharp {...entityIconProps} />,
  zone: <DesktopComputerIcon {...entityIconProps} />
});

export interface GeneralPanelProps {
  entity: "workspace" | "meeting_room" | "parking" | "area" | "zone";
  areaList: ReturnType<typeof useAreas>["areas"];
  defaultValues: Partial<EditResourceForm>;
  refSubmit: React.RefObject<HTMLButtonElement | null>;
  resource: IResource | null;
  form: UseFormReturn<EditResourceForm>;
}

export const GeneralPanel = ({
  resource,
  refSubmit,
  entity,
  areaList,
  form
}: GeneralPanelProps) => {
  const { setValue, watch } = form;

  const amenitiesResponse = useGetAmenities();
  const amenities = amenitiesResponse?.data?.result?.tags || [];

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const currentIsAvailable = watch("is_unavailable");
  watch("is_open");

  useEffect(() => {
    const keyBinding = (e: KeyboardEvent) => {
      if (
        e.key === "a" &&
        e.target instanceof Element &&
        e.target.tagName.toLowerCase() !== "input" &&
        e.target.className.toLowerCase().indexOf("prosemirror") < 0
      ) {
        setValue("is_unavailable", !currentIsAvailable);
        if (refSubmit.current) {
          refSubmit.current.focus();
        }
      }
    };
    document.addEventListener("keydown", keyBinding);
    return () => document.removeEventListener("keydown", keyBinding);
  }, [currentIsAvailable, setValue, refSubmit]);

  const isParking =
    resource?.type === "parking_space" || resource?.type === "parking_lot";
  // Dedicated resources are booked for a single owner, not walked up to —
  // the two flags are mutually exclusive (see ResourceSettingsPanel).
  const isDedicated = !!resource?.is_dedicated;

  const customParkingVerbiage = useCustomParkingVerbiage();
  const { parkingSpaceLabel, parkingLotLabel } = useMemo(() => {
    const parkingSpaceLabel = customParkingVerbiage ?? "parking space";
    const parkingLotLabel = customParkingVerbiage
      ? `${customParkingVerbiage} group`
      : "parking lot";
    return { parkingSpaceLabel, parkingLotLabel };
  }, [customParkingVerbiage]);

  const areasWithoutThisOne = useMemo(
    () =>
      areaList?.options.filter(
        (x: { value: string }) => x.value !== resource?.id
      ) ?? [],
    [areaList?.options, resource?.id]
  );
  const areaType = resource?.parent_id === null ? "floor" : "zone";

  const isMultiple = watch("is_multiple");
  const isApprovable = watch("is_approvable");

  const parentIdItems = useMemo(() => {
    const options =
      resource?.type === "area" ? areasWithoutThisOne : areaList?.options || [];
    return options.map((opt: { value: string; label: string }) => ({
      id: opt.value,
      name: opt.label
    }));
  }, [resource?.type, areasWithoutThisOne, areaList?.options]);

  const resourceMediaUrl = resource ? getResourceCoverUrl(resource) : undefined;

  if (!resource) return null;

  return (
    <div className="pr-2">
      <HookFormHiddenInput form={form} name="maximum_slots_per_user" />

      <Stack className="my-4">
        <p className="select-all font-mono text-[10px] text-neutral-400">
          ID: {resource.id}
        </p>
        {/* Cover Image Upload */}
        {resource.type !== "area" && (
          <HookFormImageUploaderInput
            form={form}
            name="cover_image_file"
            label="Cover Image"
            previewUrl={resourceMediaUrl}
            fallback={EntityIcons[entity]}
            recommendedSize="Recommended size: 812px by 480px"
          />
        )}

        <HookFormTextInput form={form} name="name" label="Name" isRequired />

        {resource.type !== "area" && (
          <>
            <div
              className={cn(
                !isLabelFieldEnabled(authenticatedUser) && "hidden"
              )}
            >
              <HookFormTextInput form={form} name="label" label="Label" />
            </div>

            <HookFormTagsInput
              form={form}
              name="amenities"
              label="Amenities"
              tags={amenities.map((amenity) => ({
                value: amenity.id,
                label: amenity.name,
                keywords: [amenity.name]
              }))}
              placeholder="Search for amenities"
            />

            <HookFormTextEditorInput
              form={form}
              name="description"
              label="Description"
            />
          </>
        )}

        {resource.type !== "meeting_room" && resource.type !== "area" && (
          <>
            <HookFormToggleInput
              form={form}
              name="is_multiple"
              label={
                isParking
                  ? `${capitalize(customParkingVerbiage ?? "Parking")} Group`
                  : "Group of Workspaces"
              }
              description={
                isParking
                  ? `Enable this to create a ${customParkingVerbiage} ${customParkingVerbiage ? "group" : "lot"} with two or more spaces instead of a single ${customParkingVerbiage}.`
                  : "Enable this to create a group of desks instead of a single workspace."
              }
              togglePosition="right"
            />

            {isMultiple && (
              <HookFormNumberInput
                form={form}
                name="total_slots"
                label={
                  isParking
                    ? `How many ${parkingSpaceLabel} are available on this ${parkingLotLabel}?`
                    : "How many total workspaces do you want to make available for reservations?"
                }
                isRequired
                min={1}
              />
            )}
          </>
        )}

        {areaList && !(resource.type === "area" && areaType === "floor") && (
          <HookFormUnifiedSelectInput
            form={form}
            name="parent_id"
            label="Parent Zone or Floor"
            description={
              resource.type !== "meeting_room" && resource.type !== "area"
                ? `Organize your ${
                    isParking && customParkingVerbiage
                      ? pluralize(customParkingVerbiage, 2)
                      : RESOURCES_TEXT[entity].plural
                  } by assigning them to an office zone or floor.`
                : "A zone must belong to a floor or another zone"
            }
            items={parentIdItems}
            placeholder="Choose an office zone or floor..."
          />
        )}

        {resource.type === "meeting_room" && (
          <HookFormNumberInput
            form={form}
            name="total_slots"
            label="Room occupancy limit"
            isRequired
            min={1}
          />
        )}

        {resource.type !== "area" && (
          <>
            <HookFormToggleInput
              form={form}
              name="is_approvable"
              label="Approval Required"
              togglePosition="right"
              description={
                entity === "zone"
                  ? `When this box is checked, reservations for workspaces belonging to ${resource?.name || "this zone"} will need to be approved.`
                  : `When this box is checked, reservations for ${resource?.name || "this workspace"} will need to be approved.`
              }
            />

            {!isDedicated && resource && (
              <ResourceTicketTypesField
                form={form}
                name="ticket_type_ids"
                resourceId={resource.id}
                officeId={resource.office_id}
                isApprovable={isApprovable}
              />
            )}

            {!isDedicated && (
              <HookFormToggleInput
                form={form}
                name="is_walkup"
                label="Walk-up"
                togglePosition="right"
                description={`Walk-up resources can't be reserved from the app. Occupancy for ${resource?.name || "this resource"} is managed externally via the API.`}
              />
            )}

            <ResourceMakeUnavailableControl
              form={form}
              resource={resource}
              entity={entity}
              name="is_unavailable"
            />
          </>
        )}
      </Stack>
    </div>
  );
};
